import { partial } from 'underscore'
import { html, render } from 'lit-html'
import { repeat } from 'lit-html/directives/repeat'
import { ifDefined } from 'lit-html/directives/if-defined'
import { unsafeHTML } from 'lit-html/directives/unsafe-html'
import { register } from '@polleverywhere/hmr-custom-element'
import { cleanString } from '@polleverywhere/core/lib/util'
import GlyphMap from '@polleverywhere/icons/dist/glyphs'
import '@polleverywhere/pe-checkbox'
import '@polleverywhere/icon-element'
import './style.scss'

function cleanTitle (title) {
  // strip out all html except for 'em' which we support in poll titles.
  // since the title is already bolded, we strip out <strong> as well.
  return cleanString(title, { whitelist: ['em'] })
}

function createEvent (eventName, targetEl, model) {
  return new CustomEvent(eventName, {
    bubbles: true,
    detail: Object.assign({ target: targetEl }, model)
  })
}

function handleActionClick (model, e) {
  const actionName = e.currentTarget.getAttribute('name')
  const eventName = `click:${actionName}`

  this.dispatchEvent(createEvent(eventName, e.target, model))
}

function handleIconClick (model, e) {
  // Do nothing if in a checkable state.
  const checkable = this.hasAttribute('checkable')
  if (checkable) { return }

  // Otherwise, re-route to trigger an item click.
  handleContentClick.call(this, model, e)
}

function handleCheckboxChange (model, e) {
  const checkbox = e.target
  model.checked = checkbox.checked
  this.dispatchEvent(createEvent('check:item', e.target, model))
}

function handleContentClick (model, e) {
  const checkable = this.hasAttribute('checkable')
  const clickable = this.hasAttribute('clickable')

  if (checkable && !clickable) {
    // Re-route to trigger a checkbox click
    const rowEl = e.currentTarget.parentElement
    rowEl.querySelector('input[type=checkbox]').click()
    return
  }

  this.dispatchEvent(createEvent('click:item', e.target, model))
}

function iconTemplate (glyph, title = '') {
  const isLegacyGlyph = !(glyph in GlyphMap)
  const type = isLegacyGlyph ? 'legacy-glyph' : 'glyph'
  return glyph ? html`
    <icon-element type='${type}' name='${glyph}'
      title='${title}'></icon-element>` : ''
}

function rowIconTemplate (model, icon, checkable) {
  if (model.skeleton) {
    return html`
      <div name='icon-wrapper'>
        <div class="element-skeleton-progress"></div>
      </div>
    `
  }

  // Only override title if showing checkbox. Otherwise, fallback to row title.
  const title = checkable
    ? `Select "${cleanTitle(model.title)}"`
    : cleanTitle(model.title)

  return icon || checkable ? html`
    <div name='icon-wrapper' title='${title}'
      @click=${partial(handleIconClick, model)}>
      ${checkable ? html`
        <pe-checkbox focus-indicator
          ?checked=${model.checked}
          @change=${partial(handleCheckboxChange, model)}></pe-checkbox>
      ` : icon}
    </div>
  ` : ''
}

function rowContentTemplate (model, icon, checkable, clickable, readonly) {
  if (model.skeleton) {
    return html`
      <div name='content'>
        <div name='title'>
          <div class="element-skeleton-progress"></div>
        </div>
        ${model.subtitles ? html`<ul name='subtitles'>
          <div class="element-skeleton-progress"></div>
        </ul>` : ''}
      </div>
    `
  }

  // strip out all html except for 'em' which we support in poll titles.
  // since the title is already bolded, we strip out <strong> as well.
  const title = cleanTitle(model.title)
  const tabindex = !checkable || clickable ? 0 : -1
  const content = html`
    <div name='title'>${unsafeHTML(title)}</div>
    <ul name='subtitles'>
      ${icon && checkable ? html`
        <li name='subicon'>${icon}</li>` : ''}
      ${model.subtitles ? model.subtitles.map((text) => html`
        <li>${cleanString(text)}</li>`) : ''}
    </ul>
  `
  if (readonly) {
    return html`
      <div name='content'>
        ${content}
      </div>
    `
  } else {
    return html`
      <button name='content'
        title='${title}'
        tabindex='${ifDefined(tabindex)}'
        @click=${partial(handleContentClick, model)}>
        ${content}
      </button>
    `
  }
}

function rowActionsTemplate (model, actions) {
  return actions.length ? html`
    <div name='actions'>${actions.map((action) => html`
      <button name='${action.name}'
        title="${action.label}"
        @click=${partial(handleActionClick, model)}>
        ${iconTemplate(action.glyph)}
      </button>`)}
    </div>
  ` : ''
}

function rowTemplate (element, model, idx) {
  if (model.skeleton) {
    return html`
      <li name='row'>
        ${rowIconTemplate(model)}
        ${rowContentTemplate(model)}
      </li>
    `
  }

  const checkable = element.hasAttribute('checkable')
  const clickable = element.hasAttribute('clickable')
  const readonly = element.hasAttribute('readonly') || model.disabled
  const icon = iconTemplate(model.glyph)
  return html`<li name='row' ?data-disabled='${model.disabled}'>
    ${rowIconTemplate(model, icon, checkable)}
    ${rowContentTemplate(model, icon, checkable, clickable, readonly)}
    ${rowActionsTemplate(model, element.additionalActions)}
  </li>`
}

function template (el, collection) {
  const keyFunction = (model) => model.id

  return html`
    ${repeat(collection, keyFunction, rowTemplate.bind(null, el))}
  `
}

function renderTemplate (el) {
  if (!el.isConnected) return
  render(template(el, el.collection), el, {
    eventContext: el
  })
}

/**
 * Item list element lets consumers render a list of objects into rows with an
 * icon, title, subtitle, checkboxes and additional custom controls.
 *
 * ```
 * <item-list .collection="${collection}"></item-list>
 * <item-list .collection="${collection}" checkable></item-list>
 * <item-list .collection="${collection}" checkable clickable></item-list>
 * <item-list .collection="${collection}" readonly></item-list>
 * ```
 *
 * @element item-list
 *
 * @attr {boolean} checkable - Renders checkboxes for each row item. When
 *    combined with `clickable`, `check:item` will only be emitted if the
 *    checkbox is clicked.
 * @attr {boolean} clickable - Augments `checkable` mode to differentiate clicks
 *    on the row item versus clicks on the checkbox.
 * @attr {boolean} readonly - Doesn't render content as a button, not clickable
 *
 * @prop {Array} collection - List of item objects to render as rows. Item
 *    objects support the following properties:
 *    - {number} id Required and must be unique
 *    - {string} title Main row title
 *    - {Array.<string>} subtitles List of strings to render as subtext
 *    - {string} glyph Name of glyph. Uses legacy if v2 glyph does not exist
 *    - {boolean} checked Starts out checked when in `checkable` mode
 * @prop {Array} additionalActions - Renders additional controls for each row
 *
 * @fires {string} click:item - Fires when row item is clicked.
 * @fires {string} check:item - Fires when row item is checked.
 * @fires {string} click:<action_name> - If additional actions are provided,
 *    emits click event based on the action name.
 */
export default class ItemList extends HTMLElement {
  static get tagName () {
    return 'item-list'
  }

  static get observedAttributes () {
    return [
      'checkable',
      'clickable',
      'readonly'
    ]
  }

  get collection () {
    return this._collection || []
  }

  set collection (collection) {
    this._collection = collection
    renderTemplate(this)
  }

  get additionalActions () {
    return this._additionalActions || []
  }

  set additionalActions (actions) {
    this._additionalActions = actions
    renderTemplate(this)
  }

  connectedCallback () {
    renderTemplate(this)
  }

  attributeChangedCallback () {
    renderTemplate(this)
  }
}

register(module, ItemList.tagName, ItemList)
